<template>
    <Tooltip
        v-if="userCanManageRestrictedEditables"
        class="editable-restriction-control"
        content="Allows team to make updates"
        placement="bottom-end"
        :delay="tooltipDelay"
    >
        <Switch
            :loading="isSettingEditableRestriction"
            size="small"
            :model-value="!editable.restricted"
            @click.stop
            @onChange="toggleEditableIsRestricted"
        />
    </Tooltip>
</template>

<script lang="ts">
import { defineComponent, h as hVue } from "vue";

import { USER_ACTIONS } from "shared-utils/enums/user";

import AddEditableDerestriction from "@/apollo/mutations/v2/AddEditableDerestriction.gql";
import RemoveEditableDerestriction from "@/apollo/mutations/v2/RemoveEditableDerestriction.gql";
import { CampaignAction } from "@/store/modules/campaign";
import EditableRestrictionMessage from "@/components/Campaign/Editables/EditableRestrictionMessage.vue";
import { userCan } from "@/services/Auth";

export default defineComponent({
    name: "EditableRestrictionControl",
    props: {
        editable: {
            type: Object,
            required: true
        }
    },

    emits: [],

    data() {
        return {
            isSettingEditableRestriction: false,
            tooltipDelay: 1000
        };
    },

    computed: {
        userCanManageRestrictedEditables() {
            return userCan(USER_ACTIONS.canManageRestrictedEditables, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        }
    },

    methods: {
        async toggleEditableIsRestricted() {
            if (!this.isSettingEditableRestriction) {
                this.isSettingEditableRestriction = true;
                try {
                    const variables = {
                        campaignId: this.campaignId,
                        editableId: this.editable._id
                    };

                    if (this.editable.restricted) {
                        await this.$apollo.mutate({
                            mutation: AddEditableDerestriction,
                            variables
                        });

                        this.$snackbar.render({
                            duration: 6,
                            render: (h: typeof hVue) =>
                                h(EditableRestrictionMessage, {
                                    label: this.editable.label,
                                    roleName: this.$store.state.auth.me?.role.name
                                })
                        });
                    } else {
                        await this.$apollo.mutate({
                            mutation: RemoveEditableDerestriction,
                            variables
                        });

                        this.$snackbar.success(`Updates disabled for ${this.editable.label}`);
                    }

                    this.$store.dispatch(CampaignAction.SetEditableRestriction, {
                        editableId: this.editable._id,
                        isRestricted: !this.editable.restricted
                    });
                } catch {
                    this.$snackbar.error(`Restrictions update failed for ${this.editable.label}`);
                }
                this.isSettingEditableRestriction = false;
            }
        }
    }
});
</script>

<style lang="scss">
.editable-restriction-control {
    line-height: 18px;
}
</style>
